import { useCallback, useEffect, useRef } from 'react';

// Poll cadence + ceiling — mirrors the related-parties hook (10s × 20 attempts ≈ 3 min 20s).
// The overview API now exposes the AION_INGEST_GST_PROFILE step status inline
// (polus mergeGstOverview), so each tick can decide whether to keep polling
// based on the response. The cap is a defensive ceiling against an orphaned
// step that never transitions to COMPLETE/FAILED. RECUR-26035.
const POLLING_INTERVAL_MS = 10000;
const POLLING_LIMIT = 20;

/**
 * Lightweight polling driver for the GST Overview tab. Caller decides when to
 * start/stop polling by toggling `shouldPoll`; the hook owns the interval
 * lifecycle, attempt cap, and stable callback handling.
 *
 *   - shouldPoll=true   → fire `refetch(true)` immediately, then every 10s
 *                         until POLLING_LIMIT is hit or shouldPoll flips off
 *   - shouldPoll=false  → clear the interval
 *
 * The latest `refetch` is stashed in a ref so the interval always calls the
 * current closure (no stale captures across renders).
 */
export function useGstOverviewPolling(refetch: (isPollingCall: boolean) => void, shouldPoll: boolean) {
    const apiPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const attemptsRef = useRef(0);
    const refetchRef = useRef(refetch);

    useEffect(() => {
        refetchRef.current = refetch;
    }, [refetch]);

    const stopPolling = useCallback(() => {
        if (apiPollingRef.current) {
            clearInterval(apiPollingRef.current);
            apiPollingRef.current = null;
        }
        attemptsRef.current = 0;
    }, []);

    useEffect(() => {
        if (!shouldPoll) {
            stopPolling();
            return;
        }
        // Polling signal just arrived — fire one immediate refetch, then set up the loop.
        refetchRef.current(true);
        if (!apiPollingRef.current) {
            attemptsRef.current = 0;
            apiPollingRef.current = setInterval(() => {
                attemptsRef.current += 1;
                if (attemptsRef.current >= POLLING_LIMIT) {
                    stopPolling();
                    return;
                }
                refetchRef.current(true);
            }, POLLING_INTERVAL_MS);
        }
        return () => stopPolling();
    }, [shouldPoll, stopPolling]);
}
